/** @file evaluateArithmetic: a safe evaluator for + - * / ( ) and decimals; no eval, null on any error. */

type Parser = { tokens: string[]; pos: number };

const TOKEN = /\s*(\d+\.?\d*|\.\d+|[-+*/()])/y;

/** Splits `text` into number and operator tokens; null if anything else (letters, `e`, `,`) is in it. */
function tokenize(text: string): string[] | null {
  const tokens: string[] = [];
  TOKEN.lastIndex = 0;
  let end = 0;
  for (let m = TOKEN.exec(text); m !== null; m = TOKEN.exec(text)) {
    tokens.push(m[1] ?? '');
    end = TOKEN.lastIndex;
  }
  return text.slice(end).trim() === '' ? tokens : null;
}

/** A number or a parenthesised expression; NaN is the failure value and poisons every sum and product above it. */
function primary(p: Parser): number {
  const t = p.tokens[p.pos];
  if (t === undefined) return Number.NaN;
  p.pos += 1;
  if (t === '(') {
    const v = expression(p);
    if (p.tokens[p.pos] !== ')') return Number.NaN;
    p.pos += 1;
    return v;
  }
  return /^[\d.]/.test(t) ? Number(t) : Number.NaN;
}

/** Leading `+` / `-` signs, any number of them. */
function unary(p: Parser): number {
  const t = p.tokens[p.pos];
  if (t === '-' || t === '+') {
    p.pos += 1;
    const v = unary(p);
    return t === '-' ? -v : v;
  }
  return primary(p);
}

/** `unary (('*' | '/') unary)*`; a zero divisor gives NaN. */
function term(p: Parser): number {
  let v = unary(p);
  for (let t = p.tokens[p.pos]; t === '*' || t === '/'; t = p.tokens[p.pos]) {
    p.pos += 1;
    const r = unary(p);
    v = t === '*' ? v * r : r === 0 ? Number.NaN : v / r;
  }
  return v;
}

/** `term (('+' | '-') term)*`. */
function expression(p: Parser): number {
  let v = term(p);
  for (let t = p.tokens[p.pos]; t === '+' || t === '-'; t = p.tokens[p.pos]) {
    p.pos += 1;
    const r = term(p);
    v = t === '+' ? v + r : v - r;
  }
  return v;
}

/** The value of the whole `text`, or null when it is not pure arithmetic, divides by zero or is not finite. */
export function evaluateArithmetic(text: string): number | null {
  const tokens = tokenize(text);
  if (tokens === null || tokens.length === 0) return null;
  const p: Parser = { tokens, pos: 0 };
  const v = expression(p);
  return p.pos === tokens.length && Number.isFinite(v) ? v : null;
}
